import { getApiClient } from '../apiClient';
import { AuditHistoryRequest, AuditHistoryResponse } from '../models/EventHistoryModels';
import {
  ConfigureIdpRequest,
  GetIdpsResponse,
  GetRolesResponse,
  IdpResponse,
  SamlProviderConfig,
  ValidateIdpRequest,
  ValidateIdpResponse,
} from '../models/IdpModels';
import {
  CreateTenantRequest,
  Tenant,
  ValidateIdentifierRequest,
  ValidateIdentifierResponse,
} from '../models/TenantModels';
import {
  DomainsResponse,
  OidcMetadataRequest,
  OidcMetadataResponse,
  PagingRequest,
  TaniumInstance,
  PermissionsResponse,
} from '../types';

// TODO(AMP-214): Remove mock data when we are ready
const useMockApiData = process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_MOCK_API_DATA === 'true';

export interface AuthClient {
  getPermissions(): Promise<PermissionsResponse>;

  getTenant(): Promise<Tenant>;

  getLinkedInstances(): Promise<TaniumInstance[]>;

  getIdps(pagingRequest: PagingRequest): Promise<GetIdpsResponse>;

  createIdp(idp: ConfigureIdpRequest): Promise<IdpResponse>;

  updateIdp(idp: ConfigureIdpRequest): Promise<void>;

  getDomains(pagingRequest: PagingRequest): Promise<DomainsResponse>;

  deleteDomain(domainId: string): Promise<void>;

  checkDomain(domainId: string): Promise<void>;

  deleteIdp(idpId: string): Promise<void>;

  validateIdp(validateReq: ValidateIdpRequest): Promise<ValidateIdpResponse>;

  getRoles(): Promise<GetRolesResponse>;

  getIdpSigningCert(): Promise<string>;

  getServiceProviderMetadataXml(idpName: string): Promise<string>;

  getSamlConfig(idpName: string): Promise<SamlProviderConfig>;

  getEventHistory(auditHistoryRequest: AuditHistoryRequest): Promise<AuditHistoryResponse>;

  deleteLinkedInstance(instanceId: string): Promise<void>;

  addDomain(domainName: string): Promise<void>;

  downloadFile(href: string): Promise<Blob>;
}

const auth = {
  getOidcMetadata: async (oidcMetadataRequest: OidcMetadataRequest): Promise<OidcMetadataResponse> => {
    const apiClient = getApiClient();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { data } = await apiClient.get<OidcMetadataResponse>('/v1/auth/oidc/metadata', {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      params: oidcMetadataRequest,
    });
    return Promise.resolve(data);
  },
};

const tenants = {
  createTenant: async (email: string, username: string, workspace: string, region: string): Promise<Tenant> => {
    /* istanbul ignore next */
    if (useMockApiData) {
      // sleep a bit to see the loading effect
      await new Promise((r) => {
        setTimeout(r, 1000);
      });
      if (username === 'error') {
        return Promise.reject(new Error('Something went wrong'));
      }
      return Promise.resolve({} as Tenant);
    }

    const createTenantRequest: CreateTenantRequest = {
      email,
      username,
      workspace,
      region,
    };

    const apiClient = getApiClient();
    const { data } = await apiClient.post<Tenant>('/v1/tenants', createTenantRequest);
    return Promise.resolve(data);
  },
  validateIdentifier: async (identifier: string): Promise<ValidateIdentifierResponse> => {
    const apiClient = getApiClient();
    const validateIdentifierRequest: ValidateIdentifierRequest = { workspace: identifier };
    const { data } = await apiClient.post<ValidateIdentifierResponse>('/v1/workspace', validateIdentifierRequest);
    return Promise.resolve(data);
  },
  getBlockedEmailDomains: async (): Promise<string[]> => {
    const apiClient = getApiClient();
    const { data } = await apiClient.get<string[]>('/v1/emaildomains/blocklist');
    return Promise.resolve(data);
  },
  sendRecoverUsernameEmail: async (email: string): Promise<void> => {
    const apiClient = getApiClient();
    await apiClient.get<string[]>(`/v1/recover-username/${email}`);
  },
};

const downloadLink = {
  serviceProviderMetadataXmlPath: '/v1/idp-setup/service-provider-metadata.xml',
  serviceProviderCertPath: '/v1/idp-setup/service-signing-cert.crt',
};

export const Api = {
  auth,
  tenants,
  downloadLink,
};
